import * as v from "valibot";

const previewsSchema = v.pipe(
  v.unknown(),
  v.transform((it) => (typeof it === "string" ? JSON.parse(it) : it)),
  v.record(
    v.string(),
    v.pipe(
      v.union([v.string(), v.number()]),
      v.transform((input) => new Date(input)),
    ),
  ),
);

const PREVIEW_MONTHS = 3;

const METADATA_KEY = "product-previews";

function useQueryKey() {
  const { user } = useAuthorizeStore();

  return [user?.userId, METADATA_KEY];
}

export const serializer = {
  deserialize: (value: string) => {
    const parsed = v.safeParse(previewsSchema, value);
    if (parsed.success) {
      return new Map(Object.entries(parsed.output));
    }
    handleError(parsed.issues, "Error parsing product previews", false);
    return null;
  },
  serialize: (value: Map<string, Date>) =>
    JSON.stringify(Object.fromEntries(value)),
};

export function useProductPreviews() {
  const { loggedIn } = useAuthorizeStore();

  const queryKey = useQueryKey();

  const api = useMetadataApi();

  return useQuery({
    queryKey,
    enabled: loggedIn,
    async queryFn() {
      const data = await api.getUserMetaData(METADATA_KEY);

      if (data?.value) {
        const parsed = serializer.deserialize(data.value);
        if (parsed) {
          return parsed;
        }
      }

      return new Map<string, Date>();
    },
  });
}

const MUTATION_NAME = "enablePreview";

export function useEnableProductPreview() {
  const { loggedIn } = useAuthorizeStore();

  const queryKey = useQueryKey();

  const previews = useProductPreviews();

  const queryClient = useQueryClient();

  const api = useMetadataApi();

  const toast = inject(keys.toast, null);

  return useMutation({
    mutationKey: [MUTATION_NAME],
    meta: {
      mutationName: MUTATION_NAME,
    },
    async mutationFn(sku: ISBN) {
      if (!loggedIn) {
        return;
      }

      const { data: existingPreviews } = await previews.suspense();

      if (existingPreviews?.has(sku)) {
        return;
      }

      const newData = new Map(existingPreviews);

      newData.set(sku, addMonths(new Date(), PREVIEW_MONTHS));

      await api.createOrUpdateMetadata(
        METADATA_KEY,
        serializer.serialize(newData),
      );

      queryClient.setQueryData(queryKey, newData);
    },
    onSuccess() {
      toast?.success({
        message:
          "Ditt vurderingseksemplar er bestilt og aktivert, du finner det på Min side.",
        buttons: [
          {
            text: "Gå til Min side",
            onClick: () => {
              navigateTo("/minside#vurderingseksemplarer");
            },
          },
        ],
      });
    },
  });
}
